import React, { useState, useEffect, useRef } from 'react';
import Select from "react-select";
import { Eye, EyeOff, Loader, Lock } from 'lucide-react';
import Spacing from '../Spacing';
import { fetchAllLocations } from "../../services/patient";
import { can_visit, notify } from "../../helpers";
import { AddAndUpdatePasswordManager } from '../../services/passwordManager';
import { Drawer } from 'vaul';

export default function AddAndEditPasswordModal(props) {
  const {    
    account_id,
    location_id,
    user_id,
    reloadData = () =>{},
    reloadDataFilter = {},
    activeModal = false,
    setActiveModal = () => {},
    editValue = {},
    editModal = false,
    previousScrollPosition = 0,
  } = props;

  const defaultValues = {
    account_id: account_id,
    location_id: location_id,
    created_by: user_id,
    id: editValue?.id ? editValue?.id : null,
    title: editValue?.title ? editValue?.title : "",
    username: editValue?.username ? editValue?.username : "",
    password: editValue?.password ? editValue?.password : "",
    portal_url: editValue?.portal_url ? editValue?.portal_url : "",
    location_ids: editValue?.location_ids ? editValue?.location_ids : [location_id],
    share_with: editValue?.share_with ? editValue?.share_with : [],
    note: editValue?.note ? editValue?.note : "",
    everyone: editValue?.everyone ? editValue?.everyone : false
  }

  const [formData, setFormData] = useState(defaultValues);
  const [passType, setPassType] = useState("password");
  const [practiceLocations, setPracticeLocations] = useState([]);
  const [submitLoader, setSubmitLoader] = useState(false);
  const dropdownRef = useRef(null);

  const getAllLocations = async (account_id) => {
    try {
      const response = await fetchAllLocations(account_id);
      if (response?.data) {
        const allLocations = response?.data?.map((option) => ({
          value: option?.id,
          label: option?.name,
        }))
        setPracticeLocations(allLocations);
      };
    } catch (error) {
      // console.log("🚀 ~ getAllLocation: ~ error:", error);
    }
  };

  useEffect(() => {
    getAllLocations(account_id);
  }, [account_id]);

  const handleLocationChange = (selectedOption) => {
    const selectedValues = selectedOption.map((option) => option.value);
    setFormData({ ...formData, location_ids: selectedValues });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  useEffect(() => {
    setFormData({
      ...formData,
      location_id: location_id,
      account_id: account_id,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location_id, account_id]);

  const generatePassword = () => {
    const length = 12;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+~";
    let password = "";
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * charset.length);
      password += charset[randomIndex];
    }

    setFormData({
      ...formData,
      password,
    });
  };

  const handleAddPasswordSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoader(true);

    try {
      if(can_visit('password_manager') && can_visit("password_manager_create")) {
        const response = await AddAndUpdatePasswordManager(formData);
      
        if (response.detail) {
          notify('error', response.detail, 2);
        } else { 
          if(response?.data?.status === "success") {
            notify("success", response.data.message, 2);
            setFormData(defaultValues);
            reloadData(reloadDataFilter);     
            handleDrawerClose();
          } else {
            notify("error", response?.data?.message, 2);
          }
        }
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail, 2);
      }
    } finally {
      setSubmitLoader(false);
    }
  };

  const handleEditPasswordSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoader(true);

    try {
      if(can_visit('password_manager') && can_visit("password_manager_create")) {
        const response = await AddAndUpdatePasswordManager(formData);
      
        if (response.detail) {
          notify('error', response.detail, 2);
        } else { 
          if(response?.data?.status === "success") {
            notify("success", response?.data?.message, 2);
            setFormData(defaultValues);
            reloadData(reloadDataFilter);     
            handleDrawerClose();
          } else {
            notify("error", response?.data?.message, 2);
          }
        }
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail, 2);
      }
    } finally {
      setSubmitLoader(false);
    }
  };

  const onSubmitHandler = editModal ? handleEditPasswordSubmit : handleAddPasswordSubmit;

  const handleDrawerClose = () => {
    setActiveModal(false);
    window.scrollTo(0, previousScrollPosition);
  };

  return (
    <div style={{ height: "100%" }} ref={dropdownRef}>
      <Drawer.Root open={activeModal} onOpenChange={handleDrawerClose}>
        <Drawer.Trigger className="docpro_single_patiant_modal_btn">
        </Drawer.Trigger>
        <Drawer.Portal>
          <Drawer.Overlay className="docpro_drawer_overlay"/>
          <Drawer.Content className="docpro_drawer_content docpro_drawer_style_4">
            <Drawer.Title className="sr-only">{editModal ? "Edit" : "Add"} Password</Drawer.Title>
            <Drawer.Description className="sr-only">{editModal ? "Edit" : "Add"} a password</Drawer.Description>
            <div className="docpro_modal_wrap docpro_modal_lg center">
              <div className="docpro_modal">
                <form onSubmit={onSubmitHandler} style={{ background: "#FFFFFF" }}>            
                  <h2 className="docpro_fs_24">{editModal ? "Edit" : "Add New"} Password</h2>

                  <div className="docpro_grid docpro_grid_gap_20_16 docpro_grid_col_2">
                    <div className="docpro_grid_item">
                      <label className="docpro_fw_500 docpro_primary_color">
                        Title <span className="docpro_red_color">*</span>
                      </label>
                      <input type="text" className="docpro_form_field size_lg" name='title' required onChange={handleChange} value={formData?.title} />
                    </div>
                    <div className="docpro_grid_item">
                      <label className="docpro_fw_500 docpro_primary_color">
                        Location <span className="docpro_red_color">*</span>
                      </label>
                      <Select
                        required
                        isMulti
                        name="location_ids"
                        options={practiceLocations}
                        placeholder="Search..."
                        isSearchable
                        value={practiceLocations?.filter((option) => 
                          formData.location_ids?.includes(option.value)
                        ) || []}
                        onChange={handleLocationChange}
                        className="react-select-container w-full"
                        classNamePrefix="react-select"
                        filterOption={(option, rawInput) => {
                          const words = rawInput.toLowerCase().split(" ");
                          return words.every((word) =>
                            option.label.toLowerCase().includes(word)
                          );
                        }}
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: '40px',
                            borderColor: 'var(--border)',
                            borderRadius: '8px'
                          }),
                          menu: (base) => ({
                            ...base,
                            zIndex: 1050,
                            marginTop: '-2px',
                            borderRadius: '0 0 3px 3px',
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 1050,
                            padding: 0
                          }),
                        }}
                      />
                    </div>
                    <div className="docpro_grid_item">
                      <label className="docpro_fw_500 docpro_primary_color">
                        Portal URL <span className="docpro_red_color">*</span>
                      </label>
                      <input type="url" className="docpro_form_field size_lg" name='portal_url' required onChange={handleChange} value={formData?.portal_url} placeholder="https://example.com" pattern="https?://.+" />
                    </div>
                    <div className="docpro_grid_item">
                      <label className="docpro_fw_500 docpro_primary_color">
                        User Name <span className="docpro_red_color">*</span>
                      </label>
                      <input type="text" className="docpro_form_field size_lg" name='username' required onChange={handleChange} value={formData?.username} />
                    </div>
                  </div>
                  <Spacing lg="20" sm="20" />

                  <div>
                    <label className="docpro_fw_500 docpro_primary_color">
                      Password <span className="docpro_red_color">*</span>
                    </label>
                    <div className='flex items-center'>
                      <div className="docpro_form_field_with_icon w-full">
                        <input
                          type={passType}
                          className="docpro_form_field size_lg"
                          name="password"
                          value={formData?.password}
                          minLength={8}
                          onChange={handleChange}
                          style={{ borderRadius: "8px 0 0 8px", padding: '5px 5px 5px 36px' }}
                        />
                        <Lock
                          width={18}
                          height={18}
                          className="docpro_form_field_icon"
                        />
                      </div>
                      <div className='flex items-center'>
                        <div className='pointer' style={{ padding: '7px 12px', height: '40px', backgroundColor: '#c0c0c0' }}>
                          {passType === "text" ? 
                            <Eye
                              width={18}
                              height={18}
                              color='var(--secondary)'
                              onClick={() => {setPassType("password")}}
                            />
                          :
                            <EyeOff
                              width={18}
                              height={18}
                              color='var(--secondary)'
                              onClick={() => {setPassType("text")}}
                            />
                          }
                        </div>
                        <button type='button'
                          className="docpro_bordered_btn docpro_fw_600 docpro_filed_btn size_lg"
                          onClick={generatePassword}
                          style={{ height: '40px', borderRadius: "0 8px 8px 0", outline: 0 }}
                        >Generate</button>
                      </div>
                    </div>
                  </div>
                  <Spacing lg="20" sm="20" />
                  <div>
                    <label className="docpro_fw_500 docpro_primary_color">
                      Note
                    </label>
                    <textarea
                      className="docpro_form_field size_lg"
                      cols="30"
                      rows="4"
                      name='note'
                      onChange={handleChange}
                      value={formData?.note}
                    ></textarea>
                  </div>

                  <Spacing lg="32" sm="32" />
                  <div className="docpro_modal_btns small_buttons docpro_mt_auto">
                    <button
                      type='button'
                      className="docpro_bordered_btn docpro_fw_600 size_lg"
                      onClick={() => handleDrawerClose()}
                    >
                      Cancel
                    </button>
                    <button
                      type='submit'
                      className="docpro_bordered_btn docpro_fw_600 docpro_filed_btn size_lg"
                    >
                      {editModal ?
                        <span>Update</span>
                      :
                        <span>Save</span>
                      }
                      {submitLoader && (
                        <span className="docpro_loading">
                          <Loader width={20} height={20} />
                        </span>
                      )}
                    </button>

                  </div>
                </form>
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </div>
  );
}
